import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { History, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n/LanguageContext";
import { format } from "date-fns";
import { sanitizeHtml, markdownToHtml, htmlToText } from "@/lib/richtext";
import { listNoteVersions, updateNote } from "@/lib/offlineStore";

/**
 * Version history dialog. Reads from the offline-first store
 * (`listNoteVersions`) so guests AND signed-in users both see snapshots
 * — remote versions when online+signed-in, local snapshots otherwise.
 *
 * Restore applies the version to the note via `updateNote`, which also
 * saves to the backend when signed-in and queues the write when offline.
 */
export default function VersionsDialog({ noteId, open, onOpenChange, onRestored }) {
  const { t } = useT();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    if (!open || !noteId) return;
    (async () => {
      setLoading(true);
      try {
        const list = await listNoteVersions(noteId);
        setVersions(list || []);
      } finally { setLoading(false); }
    })();
  }, [open, noteId]);

  const restore = async (idx) => {
    const v = versions[idx];
    if (!v || !noteId) return;
    try {
      setRestoring(idx);
      const updated = await updateNote(noteId, {
        title: v.title || "",
        content: v.content || "",
        is_checklist: !!v.is_checklist,
        checklist_items: v.checklist_items || [],
        color: v.color || "default",
        images: v.images || [],
        attachments: v.attachments || [],
      });
      toast.success(t("versions.restored") || "Older version restored");
      onRestored?.(updated);
      onOpenChange(false);
    } catch { toast.error("Restore failed"); }
    finally { setRestoring(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="versions-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <History className="h-5 w-5 text-amber-600" />
            {t("versions.title") || "Version history"}
          </DialogTitle>
          <DialogDescription>
            {t("versions.subtitle") || "Pick a snapshot to roll back to. Mango Notes keeps the last 10 saves on your device so you can always come back."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : versions.length === 0 ? (
          <div className="py-6 text-center text-sm text-stone-500 space-y-1">
            <p>No older versions yet.</p>
            <p className="text-xs">The next time you save this note, Mango Notes will keep this version here so you can roll back.</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {versions.map((v, idx) => (
              <div key={idx} className="rounded-xl border border-border p-3 flex items-start gap-3" data-testid={`version-row-${idx}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs text-stone-500 dark:text-stone-400 mb-1">
                    <History className="h-3 w-3" />
                    <span>{safeDate(v.at)}</span>
                    {v.source === "local" && <span className="text-[10px] uppercase tracking-wider text-stone-400">· local</span>}
                  </div>
                  {v.title && <div className="font-display font-semibold text-sm truncate">{v.title}</div>}
                  <div className="text-xs text-stone-600 dark:text-stone-300 line-clamp-2">
                    {htmlToText(sanitizeHtml(markdownToHtml(v.content || "")).replace(/<\/(p|div|li|h[1-6]|blockquote)>|<br\s*\/?>/gi, "$& ")) || (v.is_checklist ? `${v.checklist_items?.length || 0} items` : "")}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full shrink-0"
                  onClick={() => restore(idx)}
                  disabled={restoring === idx}
                  data-testid={`version-restore-${idx}`}
                >
                  {restoring === idx
                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
                  {t("versions.restore") || "Restore"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function safeDate(iso) {
  try { return format(new Date(iso), "MMM d, yyyy · HH:mm"); } catch { return ""; }
}
